import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  fetchIndustryOverview,
  fetchIndustrySeries,
  fetchCreditOverlay,
  IndustryOverviewItem,
  IndustryIndicatorOverview,
  triggerIndustryFetch,
} from "../../api";
import IndustryChart from "./IndustryChart";

// ── Industry config ──────────────────────────────────────────────────

const INDUSTRY_META: Record<string, { icon: string; color: string; desc: string }> = {
  "服务业": {
    icon: "🏢",
    color: "from-blue-500/10 to-blue-600/5 dark:from-blue-500/20 dark:to-blue-600/10",
    desc: "PMI、新订单、生产指数",
  },
  "建筑行业": {
    icon: "🏗️",
    color: "from-amber-500/10 to-amber-600/5 dark:from-amber-500/20 dark:to-amber-600/10",
    desc: "PMI、开工面积、投资",
  },
  "网约车": {
    icon: "🚗",
    color: "from-emerald-500/10 to-emerald-600/5 dark:from-emerald-500/20 dark:to-emerald-600/10",
    desc: "订单量、合规率",
  },
  "宏观就业": {
    icon: "👥",
    color: "from-slate-500/10 to-slate-600/5 dark:from-slate-500/20 dark:to-slate-600/10",
    desc: "全国 / 31 大城市 / 16-24 岁青年",
  },
};

// 顶部宏观概览（总）
const MACRO_INDUSTRY = "宏观就业";
// 下方细分行业（分）—— 消费信贷承压路径各有不同客群
const SUB_ORDER = ["服务业", "建筑行业", "网约车"];

// ── Mini sparkline (SVG) ─────────────────────────────────────────────

function Sparkline({ data, color = "#1A3A5C" }: { data: { value: number }[]; color?: string }) {
  if (!data || data.length < 2) return null;

  const values = data.map((d) => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const w = 120;
  const h = 40;
  const padding = 2;

  const points = values
    .map((v, i) => {
      const x = padding + (i / (values.length - 1)) * (w - padding * 2);
      const y = h - padding - ((v - min) / range) * (h - padding * 2);
      return `${x},${y}`;
    })
    .join(" ");

  // Gradient fill area
  const areaPoints = `${padding},${h - padding} ${points} ${w - padding},${h - padding}`;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0">
      <defs>
        <linearGradient id={`spark-grad-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.2} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <polygon
        points={areaPoints}
        fill={`url(#spark-grad-${color.replace("#", "")})`}
      />
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── Change badge ─────────────────────────────────────────────────────

function ChangeBadge({ change, unit }: { change: number | null; unit: string }) {
  if (change === null || change === undefined) return <span className="text-ink-tertiary">—</span>;

  const isUp = change > 0;
  const isDown = change < 0;
  const cls = isUp
    ? "text-positive"
    : isDown
      ? "text-negative"
      : "text-ink-tertiary";

  const arrow = isUp ? "↑" : isDown ? "↓" : "";
  const formatted = unit === "%"
    ? `${arrow}${Math.abs(change).toFixed(1)}ppt`
    : `${arrow}${Math.abs(change).toFixed(1)}`;

  return <span className={`text-2xs font-mono tabular-nums font-medium ${cls}`}>{formatted}</span>;
}

// ── Industry card ────────────────────────────────────────────────────

function IndustryCard({
  data,
  isActive,
  onClick,
}: {
  data: IndustryOverviewItem;
  isActive: boolean;
  onClick: () => void;
}) {
  const meta = INDUSTRY_META[data.industry] || {
    icon: "📊",
    color: "from-zinc-500/10 to-zinc-600/5",
    desc: "",
  };

  const indicators = Object.values(data.indicators) as IndustryIndicatorOverview[];
  const primary = indicators[0]; // first indicator is the "headline"
  const secondary = indicators.slice(1, 3);

  return (
    <button
      onClick={onClick}
      className={`
        paper-panel w-full text-left transition-all duration-200 overflow-hidden cursor-pointer
        ${isActive
          ? "ring-2 ring-primary-400/50 dark:ring-primary-500/40 shadow-elevated"
          : "hover:shadow-elevated hover:-translate-y-0.5"
        }
      `}
    >
      <div className={`bg-gradient-to-br ${meta.color} px-5 py-4`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-base">{meta.icon}</span>
              <h3 className="text-sm font-semibold text-ink">{data.industry}</h3>
            </div>
            <p className="text-2xs text-ink-tertiary mt-0.5">{meta.desc}</p>
          </div>
          {primary && (
            <ChangeBadge change={primary.change} unit={primary.unit} />
          )}
        </div>

        {/* Primary indicator */}
        {primary && (
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-2xs text-ink-secondary mb-1">{primary.name}</p>
              <div className="flex items-baseline gap-1.5">
                <span className="text-2xl font-semibold tabular-nums text-ink tracking-tight">
                  {primary.latest_value !== null ? primary.latest_value : "—"}
                </span>
                <span className="text-xs text-ink-tertiary">{primary.unit}</span>
              </div>
              {primary.latest_period && (
                <p className="text-2xs text-ink-tertiary mt-0.5 font-mono tabular-nums">
                  {primary.latest_period}
                </p>
              )}
            </div>
            <Sparkline
              data={primary.sparkline || []}
              color={isActive ? "#C9A96E" : "#1A3A5C"}
            />
          </div>
        )}

        {/* Secondary indicators */}
        {secondary.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border/50 flex gap-4">
            {secondary.map((ind) => (
              <div key={ind.code} className="flex-1 min-w-0">
                <p className="text-2xs text-ink-tertiary truncate">{ind.name}</p>
                <div className="flex items-baseline gap-1 mt-0.5">
                  <span className="text-sm font-semibold tabular-nums text-ink">
                    {ind.latest_value !== null ? ind.latest_value : "—"}
                  </span>
                  <span className="text-2xs text-ink-tertiary">{ind.unit}</span>
                  <ChangeBadge change={ind.change} unit={ind.unit} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </button>
  );
}

// ── Macro employment panel (top-level overview) ─────────────────────

function MacroPanel({
  data,
  isActive,
  onClick,
}: {
  data: IndustryOverviewItem;
  isActive: boolean;
  onClick: () => void;
}) {
  const meta = INDUSTRY_META[data.industry] || {
    icon: "👥",
    color: "from-slate-500/10 to-slate-600/5",
    desc: "",
  };
  const indicators = Object.values(data.indicators) as IndustryIndicatorOverview[];

  return (
    <button
      onClick={onClick}
      className={`
        paper-panel w-full text-left transition-all duration-200 overflow-hidden cursor-pointer
        ${isActive
          ? "ring-2 ring-primary-400/50 dark:ring-primary-500/40 shadow-elevated"
          : "hover:shadow-elevated hover:-translate-y-0.5"
        }
      `}
    >
      <div className={`bg-gradient-to-br ${meta.color} px-5 py-4 sm:px-6 sm:py-5`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <span className="text-xl">{meta.icon}</span>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-[15px] font-semibold text-ink">{data.industry}</h3>
                <span className="text-[10px] font-medium uppercase tracking-wider text-ink-tertiary border border-border rounded px-1.5 py-[1px]">
                  Macro
                </span>
              </div>
              <p className="text-2xs text-ink-tertiary mt-0.5">{meta.desc}</p>
            </div>
          </div>
        </div>

        {/* Horizontal indicator row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
          {indicators.map((ind) => (
            <div key={ind.code} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-2xs text-ink-secondary truncate mb-1">{ind.name}</p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-xl font-semibold tabular-nums text-ink tracking-tight">
                    {ind.latest_value !== null ? ind.latest_value : "—"}
                  </span>
                  <span className="text-2xs text-ink-tertiary">{ind.unit}</span>
                  <ChangeBadge change={ind.change} unit={ind.unit} />
                </div>
                {ind.latest_period && (
                  <p className="text-2xs text-ink-tertiary mt-0.5 font-mono tabular-nums">
                    {ind.latest_period}
                  </p>
                )}
              </div>
              <Sparkline
                data={ind.sparkline || []}
                color={isActive ? "#C9A96E" : "#475569"}
              />
            </div>
          ))}
        </div>
      </div>
    </button>
  );
}

// ── Empty state ──────────────────────────────────────────────────────

function EmptyState({ onFetch }: { onFetch: () => void }) {
  const [fetching, setFetching] = useState(false);

  const handleFetch = async () => {
    setFetching(true);
    try {
      await onFetch();
    } finally {
      setFetching(false);
    }
  };

  return (
    <div className="paper-panel-elevated p-8 sm:p-12 text-center">
      <div className="text-4xl mb-4">📊</div>
      <h2 className="text-lg font-semibold text-ink mb-2">暂无行业数据</h2>
      <p className="text-sm text-ink-secondary mb-6 max-w-md mx-auto">
        行业指标数据尚未拉取。点击下方按钮从东方财富 API 拉取服务业、建筑行业和网约车行业的核心景气指标。
      </p>
      <button
        onClick={handleFetch}
        disabled={fetching}
        className={`
          px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 cursor-pointer
          ${fetching
            ? "bg-surface-sunken text-ink-tertiary"
            : "bg-primary-700 text-white hover:bg-primary-600 shadow-sm hover:shadow"
          }
        `}
      >
        {fetching ? "拉取中..." : "拉取行业数据"}
      </button>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────

export default function IndustryPage() {
  const [activeIndustry, setActiveIndustry] = useState<string | null>(null);

  const { data: overview, isLoading, refetch } = useQuery({
    queryKey: ["industryOverview"],
    queryFn: fetchIndustryOverview,
    staleTime: 5 * 60 * 1000,
  });

  const { data: seriesData, isLoading: seriesLoading } = useQuery({
    queryKey: ["industrySeries", activeIndustry],
    queryFn: () => fetchIndustrySeries(activeIndustry!),
    enabled: !!activeIndustry,
    staleTime: 5 * 60 * 1000,
  });

  const { data: creditOverlay, isLoading: creditLoading } = useQuery({
    queryKey: ["creditOverlay", activeIndustry],
    queryFn: () => fetchCreditOverlay(activeIndustry!),
    enabled: !!activeIndustry,
    staleTime: 10 * 60 * 1000,
  });

  const industries = overview?.industries || [];
  const macroIndustry = industries.find((i) => i.industry === MACRO_INDUSTRY);
  const subIndustries = SUB_ORDER
    .map((name) => industries.find((i) => i.industry === name))
    .filter(Boolean) as IndustryOverviewItem[];
  const orderedIndustries = [
    ...(macroIndustry ? [macroIndustry] : []),
    ...subIndustries,
  ];

  const isEmpty = !isLoading && orderedIndustries.length === 0;

  const handleFetch = async () => {
    await triggerIndustryFetch();
    refetch();
  };

  return (
    <div className="space-y-5">
      {/* Section header */}
      <section className="paper-panel-elevated overflow-hidden">
        <div className="border-b border-border px-5 py-4 sm:px-6">
          <p className="section-kicker">Industry Monitor</p>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="max-w-2xl space-y-2">
              <h1 className="text-[22px] font-semibold tracking-tight text-ink sm:text-[24px]">
                行业景气监测
              </h1>
              <p className="text-sm leading-6 text-ink-secondary">
                顶部宏观就业作为整体居民收入信号；下方服务业、建筑、网约车三个细分行业，对应消费信贷不同客群的具体承压路径。
              </p>
            </div>
            {!isEmpty && (
              <div className="flex items-center gap-2 text-2xs text-ink-tertiary shrink-0">
                <span className="rounded-full border border-border bg-surface px-2.5 py-1 font-mono tabular-nums">
                  {orderedIndustries.length} 个行业
                </span>
                <span className="rounded-full border border-border bg-surface px-2.5 py-1 font-mono tabular-nums">
                  {industries.reduce(
                    (sum, ind) => sum + Object.keys(ind.indicators).length,
                    0
                  )}{" "}
                  个指标
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-5 h-5 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
          <span className="ml-3 text-sm text-ink-secondary">加载中...</span>
        </div>
      )}

      {/* Empty state */}
      {isEmpty && <EmptyState onFetch={handleFetch} />}

      {/* Industry cards */}
      {!isLoading && orderedIndustries.length > 0 && (
        <>
          {/* Macro employment: top-level overview */}
          {macroIndustry && (
            <MacroPanel
              data={macroIndustry}
              isActive={activeIndustry === macroIndustry.industry}
              onClick={() =>
                setActiveIndustry((prev) =>
                  prev === macroIndustry.industry ? null : macroIndustry.industry
                )
              }
            />
          )}

          {/* Sub-industry cards: connected to macro via section title */}
          {subIndustries.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 px-1">
                <p className="text-label">Sub Industries</p>
                <span className="text-2xs text-ink-tertiary">
                  · 不同客群的信贷承压路径
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {subIndustries.map((ind) => (
                  <IndustryCard
                    key={ind.industry}
                    data={ind}
                    isActive={activeIndustry === ind.industry}
                    onClick={() =>
                      setActiveIndustry((prev) =>
                        prev === ind.industry ? null : ind.industry
                      )
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* Detail panel */}
          {activeIndustry && (
            <section className="space-y-3 animate-fade-in">
              <div className="flex items-center justify-between px-1">
                <div>
                  <p className="text-label">Detail View</p>
                  <h2 className="mt-1 text-lg font-semibold tracking-tight text-ink">
                    {activeIndustry} · 指标详情
                  </h2>
                </div>
                <button
                  onClick={() => setActiveIndustry(null)}
                  className="text-2xs text-ink-tertiary hover:text-ink px-2 py-1 rounded transition-colors cursor-pointer"
                >
                  收起
                </button>
              </div>

              {seriesLoading ? (
                <div className="paper-panel flex items-center justify-center py-12">
                  <div className="w-4 h-4 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
                  <span className="ml-2 text-sm text-ink-secondary">加载指标数据...</span>
                </div>
              ) : seriesData && Object.keys(seriesData.indicators).length > 0 ? (
                <IndustryChart
                  indicators={seriesData.indicators}
                  industry={activeIndustry}
                  creditSeries={creditOverlay?.credit_series}
                  correlations={creditOverlay?.correlations}
                  riskTransmission={creditOverlay?.risk_transmission}
                  creditLoading={creditLoading}
                />
              ) : (
                <div className="paper-panel p-8 text-center text-sm text-ink-tertiary">
                  暂无 {activeIndustry} 的详细数据
                </div>
              )}
            </section>
          )}
        </>
      )}

      {/* Data source footer */}
      {!isEmpty && !isLoading && (
        <div className="px-1 py-3 flex items-center justify-between text-2xs text-ink-tertiary">
          <span>
            数据来源：东方财富 · 国家统计局 / 交通运输部 &nbsp;|&nbsp; 每日自动更新
          </span>
          <button
            onClick={handleFetch}
            className="text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer"
          >
            手动刷新
          </button>
        </div>
      )}
    </div>
  );
}
